import { Injectable } from "@angular/core";
import { HttpClient } from '@angular/common/http';
import { Router } from "@angular/router";
import { HubConnection, HubConnectionBuilder, LogLevel, HubConnectionState } from "@microsoft/signalr";
import { NGXLogger } from "ngx-logger";
import { retry } from 'ts-retry-promise';

import { AppSettingsService } from "../app-settings.service";
import { EventService } from "../event.service";

import { AddMailsystemEvent } from "@core/events/add-mail-system.event";
import { AddUserEvent } from "@core/events/add-user.event";
import { EditUserEvent } from "@core/events/edit-user.event";
import { EditMailsystemEvent } from "@core/events/edit-mail-system.event";
import { AddTaskEvent } from "@core/events/add-task.event";
import { TestConnectionResultEvent } from "@core/events/test-connection-result-event";
import { TaskAssignedEvent } from "@core/events/task-assigned.event";
import { MailsystemFailureEvent } from "@core/events/mailsystem-failure.event";
import { EditMailsystemActiveEvent } from "@core/events/edit-mail-system-active.event";
import { UserConnectionEvent } from "@core/events/user-connection.event";
import { TaskCompletedEvent } from "@core/events/task-completed.event";
import { TaskUnassignedEvent } from "@core/events/task-unassigned.event";
import { TaskExtendedEvent } from "@core/events/task-extended.event";
import { AddTemplateEvent } from "@core/events/add-template.event";
import { EditTemplateEvent } from "@core/events/edit-template.event";
import { DeleteTemplateEvent } from "@core/events/delete-template.event";

@Injectable({
	providedIn: 'root',
})
export class ApiEventService extends EventService {

	private _hubConnection: HubConnection | undefined;
	private url;

	private isStopped = true;

	constructor(
		private router: Router,
		public logger: NGXLogger,
		private http: HttpClient,
		private appSettings: AppSettingsService) 
	{
		super(logger);
		this.url = appSettings.getSettings().ApiBaseUrl + 'EventHub';
	}

	public createHub() {

		this._hubConnection = new HubConnectionBuilder()
			.withUrl(this.url, { /**accessTokenFactory: () => this.oktaAuth.getAccessToken()*/ })
			.withAutomaticReconnect({
				nextRetryDelayInMilliseconds: retryContext => {
					if (retryContext.previousRetryCount < 3) {
						return 10;
					}
					else {
						return 5000;
					}
				}
			})
			.configureLogging(LogLevel.Information)
			.build();

		this._hubConnection.onclose((error) => {

			if (!this.isStopped) {

				this.logger.error(`[EventService]: Error in hubconnection`, error);
				this.startHub();
			}
			else {

				this.logger.info(`[EventService]: hubconnection stopped`);
			}
		});

		this._hubConnection.onreconnecting((error) => {

			this.logger.warn(`[EventService]: Reconnecting hubconnection`, error);

			var eventService = this;
			setTimeout(function () {

				// eventService.checkAuthenticated();
			}, 200);
		});

		this._hubConnection.onreconnected((error) => {

			this.connectedSubject.next(undefined);
			this.logger.info(`[EventService]: Reconnected hubconnection`, error);
		})
		
		this.startHub();
		 
	}

	private startHub() {
		this.isAuthenticated = true;
		if (!this.isAuthenticated) {

			return;
		}

		this.logger.log(`[EventService]: Starting hubconnection`);

		this._hubConnection.start()
			.then(() => {

				this.isStopped = false;

				this._hubConnection.on("AddMailSystemEvent", (message: AddMailsystemEvent) => {
					this.logger.log(`[EventService]: AddMailSystemEvent fired`, message);
					this.addMailSystemSubject.next(message);
				});

				this._hubConnection.on("EditMailSystemEvent", (message: EditMailsystemEvent) => {
					this.logger.log(`[EventService]: EditMailSystemEvent fired`, message);
					this.editMailSystemSubject.next(message);
				});

				this._hubConnection.on("EditMailSystemActiveEvent", (message: EditMailsystemActiveEvent) => {
					this.logger.log(`[EventService]: EditMailSystemActiveEvent fired`, message);
					this.editMailSystemActiveSubject.next(message);
				});

				this._hubConnection.on("AddTaskEvent", (message: AddTaskEvent) => {
					this.logger.log(`[EventService]: AddTaskEvent fired`, message);
					this.addTaskSubject.next(message);
				});

				this._hubConnection.on("AddUserEvent", (message: AddUserEvent) => {
					this.logger.log(`[EventService]: AddUserEvent fired`, message);
					this.addUserSubject.next(message);
				});

				this._hubConnection.on("EditUserEvent", (message: EditUserEvent) => {
					this.logger.log(`[EventService]: EditUserEvent fired`, message);
					this.editUserSubject.next(message);
				});

				this._hubConnection.on("TestConnectionResultEvent", (message: TestConnectionResultEvent) => {
					this.logger.log(`[EventService]: TestConnectionResultEvent fired`, message);
					this.testConnectionResultSubject.next(message);
				});

				this._hubConnection.on("TaskAssignedEvent", (message: TaskAssignedEvent) => {
					this.logger.log(`[EventService]: TaskAssignedEvent fired`, message);
					this.taskAssignedSubject.next(message);
				});

				this._hubConnection.on("TaskUnassignedEvent", (message: TaskUnassignedEvent) => {
					this.logger.log(`[EventService]: TaskUnassignedEvent fired`, message);
					this.taskUnassignedSubject.next(message);
				});

				this._hubConnection.on("TaskCompletedEvent", (message: TaskCompletedEvent) => {
					this.logger.log(`[EventService]: TaskCompletedEvent fired`, message);
					this.taskCompletedSubject.next(message);
				});

				this._hubConnection.on("TaskExtendedEvent", (message: TaskExtendedEvent) => {
					this.logger.log(`[EventService]: TaskExtendedEvent fired`, message);
					this.taskExtendedSubject.next(message);
				});

				this._hubConnection.on("MailsystemFailureEvent", (message: MailsystemFailureEvent) => {
					this.logger.log(`[EventService]: MailsystemFailureEvent fired`, message);
					this.mailsystemFailureSubject.next(message);
				});

				this._hubConnection.on("AddTemplateEvent", (message: AddTemplateEvent) => {
					this.logger.log(`[EventService]: AddTemplateEvent fired`, message);
					this.addTemplateSubject.next(message);
				});

				this._hubConnection.on("EditTemplateEvent", (message: EditTemplateEvent) => {
					this.logger.log(`[EventService]: EditTemplateEvent fired`, message);
					this.editTemplateSubject.next(message);
				});

				this._hubConnection.on("DeleteTemplateEvent", (message: DeleteTemplateEvent) => {
					this.logger.log(`[EventService]: DeleteTemplateEvent fired`, message);
					this.deleteTemplateSubject.next(message);
				});

				this._hubConnection.on("UserConnectionEvent", (message: UserConnectionEvent) => {
					this.logger.log(`[EventService]: UserConnectionEvent fired`, message);
					this.userConnectionSubject.next(message);
				});
			})
			.then(() => {

				this.connectedSubject.next(undefined);
			}).catch(error => {
				
				this.logger.error(`[EventService]: hub connection failed`, error)

				if (error.statusCode == 401 || error.statusCode == 403) {

					this.logger.error(`[EventService]: UnAuthorized redirecting to login.`, error);
					this.router.navigateByUrl('/login');
				}
				else {

					this.logger.error(`[EventService]: hub connection start retrying in 4 seconds`, error);
					var eventService = this;

					setTimeout(function () {

						eventService.startHub();
					}, 4000);
				}
			});
	}

	public stop() {

		this.logger.log(`[EventService]: stopping hub connection`);

		if (!this._hubConnection) return;

		if (this._hubConnection.state == HubConnectionState.Connected) {

			this.isStopped = true;
			this._hubConnection.stop();
		}
		else if (this._hubConnection.state == HubConnectionState.Connecting) {

			var eventService = this;
			setTimeout(function () { eventService.stop() }, 1000);
		}
		else {

			this.isStopped = true;
			this._hubConnection.stop();
		}
	}

	private sendToServer(methodName: string, event: any): Promise<any> {

		return retry(() => {
			if (this._hubConnection.state == HubConnectionState.Connected) {
				return Promise.resolve(1);
			}
			else {
				return Promise.reject(0);
			}
		},
			{
				retries: 10,
				delay: 1000,
				logger: (msg: string) => this.logger.log(`[EventService]: sending to server paused, not connected yet`, methodName, event)
			})
			.then(() => {
				this.logger.log(`[EventService]: sending to server`, methodName, event)
				this._hubConnection.invoke(methodName, event)
			});
	}
}